import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTeacherHours,
  setActiveFilter,
  setCustomRange,
  resetHours,
} from '../../store/teacherHoursSlice';

/**
 * TeacherHoursModal
 *
 * Shows actual vs estimated class hours for a teacher.
 * - Actual  = sum of teacherSessions (joinedAt → leftAt) — handles rejoins
 * - Estimated = planned `duration` field per class
 * - Filters: Today | This Week | This Month | Custom date range
 * - Export: CSV & Print (PDF via browser)
 */
const TeacherHoursModal = ({ isOpen, toggle, teacher }) => {
  const dispatch = useDispatch();
  const { hoursData, isHoursLoading, activeFilter, customStart, customEnd } =
    useSelector((state) => state.teacherHours);

  const [localStart, setLocalStart] = useState('');
  const [localEnd, setLocalEnd] = useState('');

  // Load data when modal opens or teacher changes
  useEffect(() => {
    if (isOpen && teacher?._id) {
      dispatch(setActiveFilter('week'));
      dispatch(fetchTeacherHours(teacher._id, 'week'));
    }
    if (!isOpen) {
      dispatch(resetHours());
    }
  }, [isOpen, teacher]);

  const handleFilter = (f) => {
    dispatch(setActiveFilter(f));
    if (f !== 'custom') {
      dispatch(fetchTeacherHours(teacher._id, f));
    }
  };

  const handleCustomApply = () => {
    if (!localStart || !localEnd) return;
    dispatch(setCustomRange({ start: localStart, end: localEnd }));
    dispatch(fetchTeacherHours(teacher._id, 'custom', localStart, localEnd));
  };

  // ─── CSV Export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!hoursData?.breakdown?.length) return;
    const rows = [
      ['Date', 'Day', 'Classes Count', 'Class Names', 'Actual Hours', 'Estimated Hours'],
      ...hoursData.breakdown.map((r) => [
        r.date, r.day, r.classes, r.classNames ? r.classNames.join('; ') : '-', r.actualDisplay, r.estimatedDisplay,
      ]),
      [],
      ['TOTAL', '', hoursData.totalClasses, '', hoursData.totalHours, hoursData.estimatedHours],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${teacher?.name || 'teacher'}_class_hours_${activeFilter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Print / PDF ─────────────────────────────────────────────────────────────
  const exportPrint = () => {
    if (!hoursData?.breakdown?.length) return;
    const filterLabel = activeFilter === 'custom'
      ? `${localStart} to ${localEnd}`
      : { today: 'Today', week: 'This Week', month: 'This Month' }[activeFilter];

    const rows = hoursData.breakdown
      .map((r) => `<tr>
        <td>${formatDate(r.date)}</td>
        <td>${r.day}</td>
        <td style="text-align:center">${r.classes}</td>
        <td>${r.classNames ? r.classNames.map(name => `<div>• ${name}</div>`).join('') : '-'}</td>
        <td style="text-align:right;color:#4e73df;font-weight:700">${r.actualDisplay}</td>
        <td style="text-align:right;color:#6c757d">${r.estimatedDisplay}</td>
      </tr>`)
      .join('');

    const html = `<!DOCTYPE html><html><head><title>Class Hours Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#333}
        h2{margin-bottom:4px} p{margin:0 0 16px;color:#666}
        .stats{display:flex;gap:16px;margin-bottom:20px}
        .stat{background:#f1f3ff;border-radius:8px;padding:12px 20px;text-align:center}
        .stat .v{font-size:22px;font-weight:800;color:#4e73df}
        .stat .l{font-size:11px;color:#666;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{background:#f8f9fc;padding:8px 12px;text-align:left;border-bottom:2px solid #e3e6f0;font-size:11px}
        td{padding:8px 12px;border-bottom:1px solid #f0f0f0}
        tr:nth-child(even){background:#fafbfd}
        .footer{margin-top:16px;font-size:12px;color:#999}
      </style></head><body>
      <h2>📊 Class Hours Report — ${teacher?.name || ''}</h2>
      <p>Period: ${filterLabel}</p>
      <div class="stats">
        <div class="stat"><div class="v">${hoursData.totalHours}</div><div class="l">Actual Hours</div></div>
        <div class="stat"><div class="v">${hoursData.estimatedHours}</div><div class="l">Estimated Hours</div></div>
        <div class="stat"><div class="v">${hoursData.totalClasses}</div><div class="l">Total Classes</div></div>
        <div class="stat"><div class="v">${hoursData.avgPerDay}</div><div class="l">Avg / Day</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Day</th>
          <th style="text-align:center">Classes</th>
          <th>Class Names</th>
          <th style="text-align:right">Actual Hours</th>
          <th style="text-align:right">Estimated Hours</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">Generated on ${new Date().toLocaleString('en-IN')}</div>
      <script>window.onload=()=>{window.print();window.close()}</script>
      </body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(html);
    w.document.close();
  };

  const filters = [
    { key: 'today', label: 'Today' },
    { key: 'week',  label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader
        toggle={toggle}
        style={{ background: '#f8f9fc', borderBottom: '1px solid #e3e6f0', padding: '16px 24px' }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>
          📊 Class Hours Report
          {teacher && (
            <span style={{ color: '#5a5c69', fontWeight: 400, marginLeft: 8 }}>
              — {teacher.name}
            </span>
          )}
        </span>
      </ModalHeader>

      <ModalBody style={{ padding: '24px' }}>

        {/* ── Filter Tabs ── */}
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilter(f.key)}
              style={{
                padding: '6px 18px',
                borderRadius: 20,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                background: activeFilter === f.key ? '#4e73df' : '#eaecf4',
                color:      activeFilter === f.key ? '#fff'    : '#5a5c69',
                transition: 'all 0.15s',
              }}
            >
              {f.label}
            </button>
          ))}

          {/* Export buttons */}
          <div className="ms-auto d-flex gap-2">
            <button onClick={exportCSV} disabled={!hoursData?.breakdown?.length} style={exportBtn('#1cc88a')}>
              ⬇ CSV
            </button>
          </div>
        </div>

        {/* ── Custom Date Range ── */}
        {activeFilter === 'custom' && (
          <div className="d-flex align-items-center gap-2 mb-3 p-3"
            style={{ background: '#f0f4ff', borderRadius: 8, border: '1px solid #d0d8f0' }}>
            <label style={labelStyle}>From</label>
            <input
              type="date"
              value={localStart}
              onChange={(e) => setLocalStart(e.target.value)}
              style={dateInput}
              max={localEnd || undefined}
            />
            <label style={labelStyle}>To</label>
            <input
              type="date"
              value={localEnd}
              onChange={(e) => setLocalEnd(e.target.value)}
              style={dateInput}
              min={localStart || undefined}
              max={new Date().toISOString().split('T')[0]}
            />
            <button
              onClick={handleCustomApply}
              disabled={!localStart || !localEnd}
              style={{
                ...exportBtn('#4e73df'),
                opacity: (!localStart || !localEnd) ? 0.5 : 1,
                cursor:  (!localStart || !localEnd) ? 'not-allowed' : 'pointer',
              }}
            >
              Apply
            </button>
          </div>
        )}

        {/* ── Loading ── */}
        {isHoursLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner color="primary" />
          </div>

        ) : !hoursData || hoursData.totalClasses === 0 ? (
          /* ── Empty State ── */
          <div className="text-center" style={{ padding: '48px 0', color: '#b7b9cc' }}>
            <i className="fa fa-clock-o fa-3x mb-3" style={{ display: 'block' }}></i>
            <p style={{ fontWeight: 600, fontSize: 15 }}>No classes found for this period</p>
            <p style={{ fontSize: 13 }}>Ended classes with recorded sessions will appear here.</p>
          </div>

        ) : (
          <>
            {/* ── Summary Cards ── */}
            <div className="row g-3 mb-4">
              {[
                { color: '#4e73df', icon: '🕐', value: hoursData.totalHours,     label: 'Actual Hours' },
                { color: '#858796', icon: '📋', value: hoursData.estimatedHours, label: 'Estimated Hours' },
                { color: '#1cc88a', icon: '📚', value: hoursData.totalClasses,   label: 'Total Classes' },
                { color: '#f6c23e', icon: '📅', value: hoursData.avgPerDay,      label: 'Avg / Day (Actual)' },
              ].map((card, i) => (
                <div className="col-3" key={i}>
                  <div style={cardStyle(card.color)}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{card.icon}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{card.value}</div>
                    <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>{card.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Daily Breakdown Table ── */}
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e3e6f0' }}>
              <table className="table mb-0" style={{ fontSize: 13, tableLayout: 'fixed', width: '100%' }}>
                <thead style={{ background: '#f8f9fc' }}>
                  <tr>
                    <th style={{ ...th, width: '12%' }}>Date</th>
                    <th style={{ ...th, width: '10%' }}>Day</th>
                    <th style={{ ...th, width: '6%', textAlign: 'center' }}>Classes</th>
                    <th style={{ ...th, width: '50%' }}>Class Details (Actual / Estimated)</th>
                    <th style={{ ...th, width: '10%', textAlign: 'right' }}>
                      Actual Hours
                      <span style={badge('#4e73df')}>Real</span>
                    </th>
                    <th style={{ ...th, width: '12%', textAlign: 'right' }}>
                      Estimated Hours
                      <span style={badge('#6c757d')}>Planned</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hoursData.breakdown.map((row, i) => {
                    const diff = row.actualMinutes - row.estimatedMinutes;
                    const diffLabel = diff === 0 ? null
                      : diff > 0 ? `+${fmt(diff)}` : `-${fmt(Math.abs(diff))}`;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfd' }}>
                        <td style={td}>{formatDate(row.date)}</td>
                        <td style={{ ...td, color: '#6e707e' }}>{row.day}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{
                            background: '#e8f4fd', color: '#2e86de',
                            borderRadius: 12, padding: '2px 10px', fontWeight: 600,
                          }}>
                            {row.classes}
                          </span>
                        </td>
                        <td style={{ ...td, color: '#6e707e' }}>
                          {row.classItems && row.classItems.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                              {row.classItems.map((item, idx) => (
                                <div key={idx} style={{ background: '#f8f9fc', border: '1px solid #e3e6f0', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                  <span style={{ fontWeight: 600, color: '#2e384d' }}>• {item.title}</span>
                                  <span style={{ fontSize: '11px', color: '#4e73df', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                                    (Act: {item.actualDisplay} / Est: {item.estimatedDisplay})
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : row.classNames && row.classNames.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {row.classNames.map((name, idx) => (
                                <span key={idx} className="badge bg-light text-dark text-start" style={{ border: '1px solid #eaecf4', whiteSpace: 'normal', fontSize: '11px', display: 'block', padding: '4px 8px', fontWeight: 500 }}>
                                  • {name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#b7b9cc' }}>—</span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#4e73df' }}>
                          {row.actualDisplay}
                          {diffLabel && (
                            <div style={{
                              fontSize: 11, fontWeight: 600,
                              color: diff > 0 ? '#1cc88a' : '#e74a3b',
                            }}>
                              ({diffLabel})
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: '#6c757d' }}>
                          {row.estimatedDisplay}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Totals Row */}
                <tfoot>
                  <tr style={{ background: '#f8f9fc', fontWeight: 700, borderTop: '2px solid #e3e6f0' }}>
                    <td style={td} colSpan={2}>TOTAL</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{
                        background: '#c8e6fd', color: '#2e86de',
                        borderRadius: 12, padding: '2px 10px', fontWeight: 700,
                      }}>
                        {hoursData.totalClasses}
                      </span>
                    </td>
                    <td style={td}></td>
                    <td style={{ ...td, textAlign: 'right', color: '#4e73df', fontSize: 14 }}>
                      {hoursData.totalHours}
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#6c757d', fontSize: 14 }}>
                      {hoursData.estimatedHours}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const cardStyle = (color) => ({
  background: color, borderRadius: 10, padding: '16px 12px',
  textAlign: 'center', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
});

const exportBtn = (color) => ({
  padding: '5px 14px', borderRadius: 6, border: 'none',
  background: color, color: '#fff', fontWeight: 600, fontSize: 12,
  cursor: 'pointer', opacity: 1,
});

const badge = (color) => ({
  display: 'inline-block', marginLeft: 6, fontSize: 9, fontWeight: 700,
  background: color, color: '#fff', padding: '1px 6px', borderRadius: 10,
  letterSpacing: 0.3, verticalAlign: 'middle',
});

const dateInput = {
  border: '1px solid #ced4da', borderRadius: 6, padding: '5px 10px',
  fontSize: 13, outline: 'none',
};

const labelStyle = { fontSize: 12, fontWeight: 600, color: '#5a5c69', whiteSpace: 'nowrap' };
const th = { padding: '10px 14px', fontWeight: 700, fontSize: 12, color: '#5a5c69', borderBottom: '2px solid #e3e6f0' };
const td = { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };

export default TeacherHoursModal;
